import type { ICalendarEvent } from '@/features/calendar/interfaces'

import { useMemo } from 'react'

import { DayCell } from '@/features/calendar/components/month-view/day-cell'
import { MonthWeekSpansRow } from '@/features/calendar/components/month-view/month-week-spans-row'

import { calculateMonthTrainingPositions, getCalendarCells } from '@/features/calendar/helpers'

import { useCalendar } from '../../contexts/use-calendar'

interface IProps {
  singleDayTrainings: ICalendarEvent[]
  multiDayTrainings: ICalendarEvent[]
}

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function CalendarMonthView({ multiDayTrainings, singleDayTrainings }: IProps) {
  const { selectedDate } = useCalendar()

  // For per-day cells, only render single-day trainings. Multi-day ones will be drawn as a single weekly span overlay.
  const cellTrainings = [...singleDayTrainings]

  const cells = useMemo(() => getCalendarCells(selectedDate), [selectedDate])

  const trainingPositions = useMemo(
    () => calculateMonthTrainingPositions(multiDayTrainings, singleDayTrainings, selectedDate),
    [multiDayTrainings, singleDayTrainings, selectedDate],
  )

  // Chunk calendar cells into weeks (rows of 7)
  const weeks: typeof cells[] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))

  return (
    <div>
      <div className="grid grid-cols-7 divide-x">
        {WEEK_DAYS.map(day => (
          <div key={day} className="flex items-center justify-center py-2">
            <span className="text-xs font-medium text-t-quaternary">{day}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1">
        {weeks.map((weekCells) => {
          const weekStart = weekCells[0].date
          const weekEnd = weekCells[weekCells.length - 1].date
          return (
            <div key={weekStart.toISOString()} className="relative grid grid-cols-7 overflow-hidden">

              {/* Day cells (single-day items) */}
              {weekCells.map(cell => (
                <DayCell key={cell.date.toISOString()} cell={cell} trainingPositions={trainingPositions} trainings={cellTrainings} />
              ))}
              {/* Overlay for multi-day spans for this week */}
              <MonthWeekSpansRow
                weekStart={weekStart}
                weekEnd={weekEnd}
                trainings={multiDayTrainings}
                trainingPositions={trainingPositions}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
